import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, Plus } from 'lucide-react';
import { PermissionsService, type Permission, type PaginatedPermissionsResponse } from '../../services/security/permissions.service';
import { PermissionsTable } from '../../components/security/PermissionsTable';
import { PermissionDetailsDrawer } from '../../components/security/PermissionDetailsDrawer';
import { CreatePermissionDrawer } from '../../components/security/CreatePermissionDrawer';
import { EditPermissionDrawer } from '../../components/security/EditPermissionDrawer';
import { NotificationToast } from '../../components/ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

export const PermissionsPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [selectedPermission, setSelectedPermission] = useState<Permission | null>(null);
  const [isCreatePermissionOpen, setIsCreatePermissionOpen] = useState(false);
  const [permissionToEdit, setPermissionToEdit] = useState<Permission | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isDrawerOpen = selectedPermission !== null;
  
  // Verificar permisos
  const canCreatePermission = hasPermission('Permisos', 'crear');
  const canViewPermissions = hasPermission('Permisos', 'ver');
  const canEditPermissions = hasPermission('Permisos', 'editar');
  const canDeletePermissions = hasPermission('Permisos', 'eliminar');
  const canAdministerPermissions = hasPermission('Permisos', 'administrar');
  
  // Verificar si el usuario tiene al menos permisos básicos para ver la página
  const hasBasicAccess = canViewPermissions || canAdministerPermissions;
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Resetear página cuando cambie el término de búsqueda o filtros
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, statusFilter]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const { 
    data, 
    isLoading, 
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedPermissionsResponse>({
    queryKey: ['permissions', page, limit, debouncedSearchTerm, statusFilter],
    queryFn: () => PermissionsService.getPermissions({
      page,
      limit,
      search: debouncedSearchTerm || undefined,
      activo: statusFilter,
    }),
    enabled: hasBasicAccess,
  });

  const permissions = data?.data || [];
  const paginationMeta = data?.meta;

  const handlePermissionClick = (permission: Permission) => {
    setSelectedPermission(permission);
  };

  const handleCloseDrawer = () => {
    setSelectedPermission(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleStatusFilterChange = (status: boolean | undefined) => {
    setStatusFilter(status);
  };

  const handleCreatePermissionSuccess = () => {
    refetch();
    setNotificationConfig({
      title: '¡Permiso creado!',
      message: 'El permiso se ha creado exitosamente',
      type: 'success'
    });
    setShowNotification(true);
  };

  const handleEditPermission = (permission: Permission) => {
    setPermissionToEdit(permission);
  };

  const handleCloseEditDrawer = () => {
    setPermissionToEdit(null);
  };

  const handleEditPermissionSuccess = () => {
    setPermissionToEdit(null);
    refetch();
    if (selectedPermission && permissionToEdit && selectedPermission.id === permissionToEdit.id) {
      setSelectedPermission(null);
    }
    setNotificationConfig({
      title: '¡Permiso actualizado!',
      message: 'El permiso se ha actualizado exitosamente',
      type: 'success'
    });
    setShowNotification(true);
  };

  const handleTogglePermissionStatus = async (permission: Permission) => {
    try {
      const updatedPermission = await PermissionsService.togglePermissionStatus(permission.id);
      refetch();
      if (selectedPermission && selectedPermission.id === permission.id) {
        setSelectedPermission(updatedPermission);
      }
      setNotificationConfig({
        title: `¡Permiso ${updatedPermission.activo ? 'activado' : 'desactivado'}!`,
        message: `El permiso se ha ${updatedPermission.activo ? 'activado' : 'desactivado'} exitosamente`,
        type: 'success'
      });
      setShowNotification(true);
    } catch (error) {
      console.error('Error al cambiar estado del permiso:', error);
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo cambiar el estado del permiso',
        type: 'error'
      });
      setShowNotification(true);
    }
  };

  const handleDeletePermission = async (permission: Permission) => {
    try {
      await PermissionsService.deletePermission(permission.id);
      refetch();
      if (selectedPermission && selectedPermission.id === permission.id) {
        setSelectedPermission(null);
      }
      setNotificationConfig({
        title: '¡Permiso eliminado!',
        message: 'El permiso se ha eliminado exitosamente',
        type: 'success'
      });
      setShowNotification(true);
    } catch (error: any) {
      console.error('Error al eliminar permiso:', error);
      setNotificationConfig({
        title: 'Error al eliminar',
        message: error?.response?.data?.message || 'No se pudo eliminar el permiso',
        type: 'error'
      });
      setShowNotification(true);
    }
  };

  // Verificar acceso básico primero
  if (!hasBasicAccess) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h1 className="text-xs font-medium text-gray-900 mb-2">Acceso denegado</h1>
          <p className="text-gray-600 mb-4">
            No tienes permisos para acceder a la gestión de permisos.
          </p>
          <p className="text-xs text-gray-500">
            Necesitas al menos el permiso "ver" o "administrar" en el módulo de Permisos.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xs mb-4">Error al cargar los permisos</div>
          <button 
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        {/* Filtros y barra de búsqueda */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Filtros de estado */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">Filtrar por:</span>
            <div className="flex space-x-1">
              <button
                onClick={() => handleStatusFilterChange(undefined)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  statusFilter === undefined
                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleStatusFilterChange(true)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  statusFilter === true
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Activos
              </button>
              <button
                onClick={() => handleStatusFilterChange(false)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  statusFilter === false
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                Inactivos
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Barra de búsqueda */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isFetching && searchTerm ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Buscar permisos..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-64 pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs placeholder-gray-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="text-xs">
                  {searchTerm ? (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-blue-700">{paginationMeta ? paginationMeta.total : permissions.length}</span>
                      <span className="text-gray-600">resultados encontrados</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium text-blue-700">{paginationMeta ? paginationMeta.total : permissions.length}</span>
                      <span className="text-gray-600">permisos registrados</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Selector de cantidad por página */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500 font-medium">Mostrar</span>
              <div className="relative" data-dropdown>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-xs font-medium text-gray-700 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer flex items-center min-w-15"
                >
                  {limit}
                </button>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
                    {[5, 10, 25, 50].map((value) => (
                      <div
                        key={value}
                        onClick={() => {
                          handleLimitChange(value);
                          setIsDropdownOpen(false);
                        }}
                        className="px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        {value}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {canCreatePermission && (
              <button
                onClick={() => setIsCreatePermissionOpen(true)}
                className="bg-blue-600 text-white px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo Permiso</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla de permisos */}
      <div className="relative">
        <PermissionsTable 
          permissions={permissions}
          onPermissionClick={handlePermissionClick}
          isLoading={isLoading && permissions.length === 0}
        />
        {isFetching && permissions.length > 0 && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-6 py-4 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-xs font-medium text-gray-700">Actualizando...</span>
            </div>
          </div>
        )}
      </div>

      {/* Paginación */}
      {paginationMeta && paginationMeta.totalPages > 1 && (
        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="text-xs text-gray-600">
                <span className="font-medium text-gray-900">
                  {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                </span>
                <span className="mx-1">–</span>
                <span className="font-medium text-gray-900">
                  {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                </span>
                <span className="mx-2">de</span>
                <span className="font-medium text-gray-900">{paginationMeta.total}</span>
                <span className="ml-1">resultados</span>
              </div>
              
              <div className="text-xs text-gray-500">
                Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Primera página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={handlePreviousPage}
                disabled={!paginationMeta.hasPrevious}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasPrevious
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página anterior"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center space-x-1 mx-2">
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 3;
                  let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                  let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                  
                  start = Math.max(1, end - maxVisiblePages + 1);

                  if (start > 1) {
                    pages.push(
                      <button
                        key="1"
                        onClick={() => handlePageChange(1)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        1
                      </button>
                    );
                    if (start > 2) {
                      pages.push(
                        <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                  }

                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-9 h-9 flex items-center justify-center text-xs font-medium rounded-lg transition-all ${
                          i === paginationMeta.page
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  if (end < paginationMeta.totalPages) {
                    if (end < paginationMeta.totalPages - 1) {
                      pages.push(
                        <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                      );
                    }
                    pages.push(
                      <button
                        key={paginationMeta.totalPages}
                        onClick={() => handlePageChange(paginationMeta.totalPages)}
                        className="w-9 h-9 flex items-center justify-center text-xs font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                      >
                        {paginationMeta.totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              <button
                onClick={handleNextPage}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Página siguiente"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={() => handlePageChange(paginationMeta.totalPages)}
                disabled={!paginationMeta.hasNext}
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                  paginationMeta.hasNext
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Última página"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de detalles del permiso */}
      <PermissionDetailsDrawer 
        isOpen={isDrawerOpen}
        permission={selectedPermission}
        onClose={handleCloseDrawer}
        onEditPermission={handleEditPermission}
        onTogglePermissionStatus={handleTogglePermissionStatus}
        onDeletePermission={handleDeletePermission}
      />

      {/* Drawer de crear permiso */}
      <CreatePermissionDrawer 
        isOpen={isCreatePermissionOpen}
        onClose={() => setIsCreatePermissionOpen(false)}
        onSuccess={handleCreatePermissionSuccess}
      />

      {/* Drawer de editar permiso */}
      <EditPermissionDrawer 
        isOpen={permissionToEdit !== null}
        permission={permissionToEdit}
        onClose={handleCloseEditDrawer}
        onSuccess={handleEditPermissionSuccess}
      />
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={5000}
        />
      )}
    </div>
  );
};